import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminChrome from "../components/AdminChrome";
import { useRequireAdmin } from "./AdminLogin";

const EMPTY = { key: "", label: "", type: "rich_text", enabled: true, order: 10, content: "" };

const PROFILE_EMPTY = { photo_kind: "avatar", photo_url: "/profile-anonymous.svg", real_name: "", age: "", occupation: "", tagline: "", bio: "", links: [] };

function initialsAvatar(name) {
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="640"><rect width="100%" height="100%" fill="#ffde16"/><text x="50%" y="54%" text-anchor="middle" font-family="Tahoma,sans-serif" font-size="220" font-weight="bold" fill="#000">${initials}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export default function AdminHeaderModules() {
  useRequireAdmin();
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/header-modules").then((r) => setModules(r.data?.items || [])).catch(() => setMsg("failed to load"));
  useEffect(() => { load(); }, []);

  const start = (module) => { setEditing(module?.id || "new"); setForm(module ? { ...module } : { ...EMPTY, order: (modules.length + 1) * 10 }); setMsg(""); };
  const profile = form.type === "profile" ? { ...PROFILE_EMPTY, ...(typeof form.content === "object" ? form.content : {}) } : null;
  const updateProfile = (key, value) => setForm({ ...form, content: { ...profile, [key]: value } });
  const uploadProfilePhoto = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateProfile("photo_url", data.url); setMsg("uploaded");
    } catch (e) { setMsg(e.response?.data?.detail || "upload failed"); }
  };
  const save = async (event) => {
    event.preventDefault();
    try {
      if (editing !== "new") await api.patch(`/admin/header-modules/${editing}`, form);
      else await api.post("/admin/header-modules", form);
      setEditing(null); setForm(EMPTY); setMsg("saved"); await load();
    } catch (e) { setMsg(e.response?.data?.detail || "save failed"); }
  };
  const remove = async (id, label) => { if (!window.confirm(`Permanently delete ${label}?`)) return; await api.delete(`/admin/header-modules/${id}`); await load(); };
  const move = async (index, direction) => {
    const next = [...modules];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setModules(next);
    await api.post("/admin/header-modules/reorder", { ids: next.map((module) => module.id) });
    await load();
  };

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-header-modules-page">
        <div className="xs-container" style={{ maxWidth: 900 }}>
          <AdminChrome />
          <h1 style={{ margin: "0 0 4px", color: "#ffde16" }}>header modules</h1>
          <p style={{ opacity: 0.8 }}>Edit the information panels shown beside the Posts link. Disabled modules remain saved.</p>
          <p style={{ opacity: 0.65, fontSize: "0.82em" }}>Profile photo note: browsers cannot block OS screenshots or direct URL fetches; the overlay and analytics detect common save gestures only.</p>
          <button className="xs-btn primary" onClick={() => start(null)} data-testid="add-header-module">+ add module</button>
          {editing !== null && (
            <form onSubmit={save} className="xs-module-form">
              <label className="xs-label">label</label>
              <input className="xs-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: form.key || e.target.value })} required />
              <label className="xs-label">key</label>
              <input className="xs-input" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} required />
              <label className="xs-label">type</label>
              <select className="xs-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="rich_text">rich text</option><option value="schedule">schedule</option><option value="contact">contact</option><option value="menu">menu</option><option value="profile">profile</option>
              </select>
              {form.type === "profile" ? (
                <div className="xs-profile-editor">
                  <label className="xs-label">photo choice</label>
                  <select className="xs-input" value={profile.photo_kind} onChange={(e) => updateProfile("photo_kind", e.target.value)}><option value="photo">Real photo</option><option value="avatar">Profile avatar</option></select>
                  <label className="xs-label">photo/avatar</label>
                  <input type="file" accept="image/*" onChange={(e) => uploadProfilePhoto(e.target.files?.[0])} />
                  <div className="xs-profile-editor-preview">{profile.photo_url ? <img src={mediaUrl(profile.photo_url)} alt="Profile preview" /> : <span>no image selected</span>}</div>
                  <button className="xs-btn ghost" type="button" onClick={() => updateProfile("photo_url", initialsAvatar(profile.real_name))}>Use initials</button>
                  <label className="xs-label">real name</label><input className="xs-input" required={form.enabled} value={profile.real_name} onChange={(e) => updateProfile("real_name", e.target.value)} />
                  <label className="xs-label">age</label><input className="xs-input" value={profile.age} onChange={(e) => updateProfile("age", e.target.value)} />
                  <label className="xs-label">occupation</label><input className="xs-input" value={profile.occupation} onChange={(e) => updateProfile("occupation", e.target.value)} />
                  <label className="xs-label">tagline</label><input className="xs-input" value={profile.tagline} onChange={(e) => updateProfile("tagline", e.target.value)} />
                  <label className="xs-label">bio</label><textarea className="xs-input" rows={6} value={profile.bio} onChange={(e) => updateProfile("bio", e.target.value)} placeholder="HTML is sanitized before display" />
                  <label className="xs-label">links (one label | URL per line)</label><textarea className="xs-input" rows={4} value={(profile.links || []).map((link) => `${link.label} | ${link.url}`).join("\n")} onChange={(e) => updateProfile("links", e.target.value.split("\n").map((line) => { const [label, url] = line.split("|"); return { label: (label || "").trim(), url: (url || "").trim() }; }).filter((link) => link.label || link.url))} />
                </div>
              ) : (
                <><label className="xs-label">content</label><textarea className="xs-input" rows={9} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="HTML is sanitized before display" /></>
              )}
              <label className="xs-label"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> enabled</label>
              <div style={{ display: "flex", gap: 8 }}><button className="xs-btn primary" type="submit">save module</button><button className="xs-btn ghost" type="button" onClick={() => setEditing(null)}>cancel</button></div>
            </form>
          )}
          {msg && <div style={{ margin: "12px 0", opacity: 0.8 }}>{msg}</div>}
          <div className="xs-module-list">
            {modules.map((module, index) => (
              <div className={`xs-module-row ${module.enabled ? "" : "disabled"}`} key={module.id}>
                <div><strong>{module.label}</strong><div style={{ fontSize: "0.8em", opacity: 0.7 }}>{module.key} · {module.type} · order {module.order}</div></div>
                <div className="xs-module-actions"><button className="xs-btn ghost" onClick={() => move(index, -1)} aria-label="Move up">↑</button><button className="xs-btn ghost" onClick={() => move(index, 1)} aria-label="Move down">↓</button><button className="xs-btn ghost" onClick={() => start(module)}>edit</button><button className="xs-btn danger" onClick={() => remove(module.id, module.label)}>delete</button></div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer show />
    </div>
  );
}
